import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, CardHeader, Alert, Spinner } from 'reactstrap';
import ReactECharts from 'echarts-for-react';
import axios from 'axios';
import LogoSpinner from '../../Components/Common/LogoSpinner';

const ActivityHeatmap = ({ dateRange, groupBy = 'daily' }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [heatmapData, setHeatmapData] = useState([]);
    const [metadata, setMetadata] = useState({
        total_activities: 0,
        peak_hour: '',
        peak_period: '',
        average_activities: 0
    });
    const [lastUpdated, setLastUpdated] = useState(null);

    const hours = ['12a', '1a', '2a', '3a', '4a', '5a', '6a', '7a', '8a', '9a', '10a', '11a',
        '12p', '1p', '2p', '3p', '4p', '5p', '6p', '7p', '8p', '9p', '10p', '11p'];

    // Y-axis labels will be determined by the API response
    const [yAxisLabels, setYAxisLabels] = useState([]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const authToken = JSON.parse(sessionStorage.getItem('authUser'))?.token;
            if (!authToken) {
                throw new Error('Authentication token not found');
            }

            const response = await axios.get(`${process.env.REACT_APP_API_URL1}analytics/dashboard/activity-heatmap`, {
                params: {
                    start_date: dateRange?.start?.toISOString(),
                    end_date: dateRange?.end?.toISOString(),
                    group_by: groupBy
                },
                headers: {
                    'Authorization': `${authToken}`
                }
            });

            if (response.data) {
                const data = response.data; // Handle both response structures
                
                // Extract unique y-axis labels based on grouping type
                const labels = [];
                const labelMap = {};
                
                // First pass: collect all unique labels
                data.heatmap_data.forEach(item => {
                    let yIndex;
                    let yLabel;
                    
                    // Determine y-axis index and label based on grouping type
                    if (groupBy === 'daily') {
                        yIndex = item.day;
                        yLabel = item.day_label || `Day ${item.day}`;
                    } else if (groupBy === 'weekly') {
                        yIndex = item.week;
                        yLabel = item.week_label || `Week ${item.week + 1}`;
                    } else if (groupBy === 'monthly') {
                        yIndex = item.month;
                        yLabel = item.month_label || `Month ${item.month + 1}`;
                    } else if (groupBy === 'yearly') {
                        yIndex = item.year;
                        yLabel = item.year_label || `Year ${item.year + 2021}`;
                    }
                    
                    // Store label if not already in the map
                    if (labelMap[yIndex] === undefined) {
                        labelMap[yIndex] = labels.length;
                        labels.push(yLabel);
                    }
                });
                
                // Second pass: transform data for ECharts
                const transformedData = [];
                data.heatmap_data.forEach(item => {
                    let yIndex;
                    
                    // Determine y-axis index based on grouping type
                    if (groupBy === 'daily') {
                        yIndex = labelMap[item.day];
                    } else if (groupBy === 'weekly') {
                        yIndex = labelMap[item.week];
                    } else if (groupBy === 'monthly') {
                        yIndex = labelMap[item.month];
                    } else if (groupBy === 'yearly') {
                        yIndex = labelMap[item.year];
                    }
                    
                    transformedData.push({
                        value: [item.hour, yIndex, item.value]
                    });
                });
                
                console.log('Y-axis labels:', labels);
                console.log('Transformed data:', transformedData);
                
                setYAxisLabels(labels);
                setHeatmapData(transformedData);
                
                // Set metadata
                const metadata = data.metadata || {};
                setMetadata({
                    total_activities: metadata.total_activities || 0,
                    peak_hour: metadata.peak_hour || '',
                    peak_period: getPeakPeriodLabel(metadata, groupBy),
                    average_activities: getAverageActivitiesValue(metadata, groupBy)
                });
                setLastUpdated(new Date().toLocaleString());
            } else {
                throw new Error('Invalid response format');
            }
        } catch (error) {
            console.error('Error fetching activity heatmap data:', error);
            setError('Failed to load activity data');
        } finally {
            setLoading(false);
        }
    }, [dateRange, groupBy]);

    // Helper function to get the peak period label based on grouping
    const getPeakPeriodLabel = (metadata, groupBy) => {
        if (groupBy === 'daily') return metadata.peak_day;
        if (groupBy === 'weekly') return metadata.peak_week;
        if (groupBy === 'monthly') return metadata.peak_month;
        if (groupBy === 'yearly') return metadata.peak_year;
        return '';
    };

    // Helper function to get the average activities value based on grouping
    const getAverageActivitiesValue = (metadata, groupBy) => {
        if (groupBy === 'daily') return metadata.average_daily_activities;
        if (groupBy === 'weekly') return metadata.average_weekly_activities;
        if (groupBy === 'monthly') return metadata.average_monthly_activities;
        if (groupBy === 'yearly') return metadata.average_yearly_activities;
        return 0;
    };

    // Fetch data on component mount and when dateRange or groupBy changes
    useEffect(() => {
        fetchData();
        
        // Set up auto-refresh every 5 minutes
        const intervalId = setInterval(fetchData, 5 * 60 * 1000);
        
        // Clean up interval on component unmount
        return () => clearInterval(intervalId);
    }, [fetchData]);

    const getOption = () => {
        // Find the maximum value for visualMap scaling
        const maxValue = heatmapData.length > 0 
            ? Math.max(...heatmapData.map(item => item.value[2]))
            : 10;
            
        return {
            tooltip: {
                position: 'top',
                formatter: function (params) {
                    const hour = hours[params.data.value[0]];
                    const period = yAxisLabels[params.data.value[1]];
                    return `
                        <strong>${period}, ${hour}</strong><br/>
                        Activities: ${params.data.value[2]}
                    `;
                }
            },
            grid: {
                height: '50%',
                top: '10%'
            },
            xAxis: {
                type: 'category',
                data: hours,
                splitArea: {
                    show: true
                }
            },
            yAxis: {
                type: 'category',
                data: yAxisLabels,
                splitArea: {
                    show: true
                }
            },
            visualMap: {
                min: 0,
                max: maxValue,
                calculable: true,
                orient: 'horizontal',
                left: 'center',
                bottom: '15%',
                inRange: {
                    color: ['#e6f7ff', '#1890ff', '#0050b3']
                }
            },
            series: [{
                name: 'User Activity',
                type: 'heatmap',
                data: heatmapData,
                label: {
                    show: false
                },
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowColor: 'rgba(0, 0, 0, 0.5)'
                    }
                }
            }]
        };
    };

    // Get the title based on grouping type
    const getTitle = () => {
        if (groupBy === 'daily') return 'Daily Activity Heatmap';
        if (groupBy === 'weekly') return 'Weekly Activity Heatmap';
        if (groupBy === 'monthly') return 'Monthly Activity Heatmap';
        if (groupBy === 'yearly') return 'Yearly Activity Heatmap';
        return 'Activity Heatmap';
    };

    // Get the period label based on grouping type
    const getPeriodLabel = () => {
        if (groupBy === 'daily') return 'Day';
        if (groupBy === 'weekly') return 'Week';
        if (groupBy === 'monthly') return 'Month';
        if (groupBy === 'yearly') return 'Year';
        return 'Period';
    };

    return (
        <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
                <div>
                    <h4 className="card-title mb-0">{getTitle()}</h4>
                    {metadata && (
                        <p className="text-muted small mt-1 mb-0">
                            Total Activities: {metadata.total_activities.toLocaleString()} | 
                            Peak Hour: {metadata.peak_hour} | 
                            Peak {getPeriodLabel()}: {metadata.peak_period} | 
                            Avg {getPeriodLabel()}: {metadata.average_activities.toLocaleString()}
                        </p>
                    )}
                </div>
            </CardHeader>
            <CardBody>
                {loading ? (
                    <div className="text-center py-5">
                        <LogoSpinner message="Loading activity data..." />
                    </div>
                ) : error ? (
                    <Alert color="danger">{error}</Alert>
                ) : heatmapData.length > 0 ? (
                    <ReactECharts 
                        option={getOption()} 
                        style={{ height: '350px' }}
                        opts={{ renderer: 'svg' }}
                    />
                ) : (
                    <div className="text-center py-5">
                        <p className="text-muted">No activity data available for the selected period.</p>
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

export default ActivityHeatmap; 